import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, X } from "react-bootstrap-icons";
import { Label } from "@radix-ui/react-label";
import { FieldWrapper } from "../Additional/FieldWrapper";
import classNames from "classnames";

export interface ComboboxOption {
    id: string;
    name: string;
}

interface ComboboxProps {
    options: ComboboxOption[];
    value: string | null;
    onChange: (id: string | null) => void;
    placeholder?: string;
    disabled?: boolean;
    label?: string;
    name: string;
    required?: boolean;
    className?: string;
}

const Combobox: React.FC<ComboboxProps> = ({
    options = [],
    value,
    onChange,
    placeholder = "Select...",
    disabled = false,
    label,
    name,
    required = false,
    className
}) => {
    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const safeOptions = Array.isArray(options) ? options : [];
    const selectedOption = safeOptions.find((o) => o.id === value) ?? null;

    const filtered = query.trim()
        ? safeOptions.filter((o) => o.name.toLowerCase().includes(query.toLowerCase()))
        : safeOptions;

    // Handle clicking outside to close
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setQuery("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSelect = (option: ComboboxOption) => {
        onChange(option.id);
        setQuery("");
        setIsOpen(false);
    };

    return (
        <div className={classNames("w-full", className)}>
            {label && (
                <Label className="block text-sm font-medium mb-2">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </Label>
            )}

            <FieldWrapper name={name}>
                <div ref={containerRef} className="relative w-full">
                    <div
                        onClick={() => { if (!disabled) setIsOpen((prev) => !prev); }}
                        className={classNames(
                            "flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1",
                            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-gray-400",
                            isOpen && "ring-1 ring-ring border-ring"
                        )}
                    >
                        {isOpen ? (
                            <input
                                autoFocus
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={selectedOption?.name ?? placeholder}
                                className="w-full bg-transparent text-sm outline-none"
                                onClick={(e) => e.stopPropagation()}
                            />
                        ) : (
                            <span className={"truncate"}>
                                {selectedOption?.name ?? placeholder}
                            </span>
                        )}

                        <div className="flex shrink-0 items-center gap-1 ml-2">
                            <ChevronDown className={classNames(
                                "h-4 w-4 transition-transform duration-200",
                                isOpen && "rotate-180"
                            )} />
                        </div>
                    </div>

                    {isOpen && (
                        <ul className="absolute z-50 mt-2 max-h-60 w-full overflow-auto rounded-md border p-1 shadow-md animate-in fade-in-0 zoom-in-95 bg-white dark:bg-gray-800">
                            {filtered.length > 0 ? (
                                filtered.map((option) => (
                                    <li
                                        key={option.id}
                                        onClick={() => handleSelect(option)}
                                        className={classNames(
                                            "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-700",
                                            option.id === value && "font-medium"
                                        )}
                                    >
                                        {option.name}
                                    </li>
                                ))
                            ) : (
                                <li className="px-2 py-1 text-sm italic text-gray-400">
                                    No results found.
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            </FieldWrapper>
        </div>
    );
};

export default Combobox;